import gql from "graphql-tag";
import { AgreementScopeWhere, AgreementSubjectMatter, AgreementTagType } from "../types/graphql-global-types";

export const SAVE_FLEX_ENTITY = gql`
	mutation SaveFlexEntity(
		$typeId: ID!
		$id: ID
		$caseIds: [ID!]
		$data: String!
		$status: FlexFormStatus!
		$isCaseDeletionAllowed: Boolean!
	) {
		SaveFlexEntity(
			id: $id
			typeId: $typeId
			caseIds: $caseIds
			data: $data
			status: $status
			isCaseDeletionAllowed: $isCaseDeletionAllowed
		) {
			kind
			transactionIds
		}
	}
`;

export const treatmentFieldsFragment = gql`
	fragment TreatmentFields on Treatment {
		Code
		GTIN
		UDI
		PharmaCode
		SwissmedicNo8
		NDC
		DIN
		Name
		LOT
		REF
		canBeMain
		activeIngredientQuantity
		uoMType
		regulatedBy {
			id
			brand {
				Name
			}
			generic {
				Name
			}
			BrandName
			GenericName
			DescriptionDe
		}
		country {
			name
		}
		prices(options: { sort: { ValidFromDate: ASC } }) {
			priceType
			ValidFromDate
			ValidToDate
			Price
			currency {
				Code
			}
		}
	}
`;

export const treatmentWithArticle71IndicationsFieldsFragment = gql`
	fragment TreatmentWithArticle71IndicationsFields on Treatment {
		Code
		GTIN
		regulatedBy {
			brand {
				isPatented
				patentExpiryDate

				mainIndications {
					validFromDate
					validToDate
					article71Indication {
						name
						approvalDate
						sllAdditionDate
					}
				}

				article71Indications {
					approvalDate
				}
			}
			BrandName
			DescriptionDe
		}
		country {
			name
		}
		prices(options: { sort: { ValidFromDate: ASC } }) {
			priceType
			ValidFromDate
			ValidToDate
			Price
		}
	}
`;

export const SAVE_FLEX_ENTITIES = gql`
	mutation SaveFlexEntities(
		$typeId: ID!
		$id: ID
		$caseIds: [ID!]
		$data: [String!]
		$status: FlexFormStatus!
		$isCaseDeletionAllowed: Boolean!
	) {
		SaveFlexEntities(
			id: $id
			typeId: $typeId
			caseIds: $caseIds
			data: $data
			status: $status
			isCaseDeletionAllowed: $isCaseDeletionAllowed
		) {
			kind
			transactionIds
		}
	}
`;

export const preparationFieldsFragment = gql`
	fragment PreparationFieldsFragment on Preparation {
		id
		AtcCode
		BrandName
		GenericName
		DescriptionDe
		brand {
			id
			Name
			isPatented
			patentExpiryDate
			article71Indications {
				id
				name
				countryScopeId
				approvalDate
				sllAdditionDate
				notes
				agreementScope {
					scopeId
				}
				agreementTemplate {
					id
					name
					formattedTags
					limitation {
						uuid
						HasPriceModel
					}
				}
				mainIndication {
					id
					validFromDate
					validToDate
				}
			}
		}
		regulates {
			Code
			GTIN
			UDI
			PharmaCode
			SwissmedicNo8
			NDC
			DIN
			activeIngredientQuantity
			uoMType
			Name
			country {
				name
			}
			regulatedBy {
				id
				brand {
					Name
				}
				BrandName
				GenericName
				DescriptionDe
			}
		}
	}
`;

export const treatmentSetFieldsFragment = gql`
	${treatmentFieldsFragment}
	${preparationFieldsFragment}

	fragment TreatmentSetFieldsFragment on TreatmentSet {
		generic {
			Name
			preparations {
				...PreparationFieldsFragment
			}
		}
		preparations {
			...PreparationFieldsFragment
		}
		treatments {
			...TreatmentFields
		}
		isFixedPriceTarget
	}
`;

export const scenarioFieldsFragment = gql`
	${treatmentFieldsFragment}

	fragment ScenarioFields on Scenario {
		order
		operator
		isGrouped
		isProRating
		isRelevantForArt71
		actionSets
		actionSetsOperator
		group
		hasMultipleBasePrices
		conditions(options: { sort: [{ order: ASC }] }) {
			order
			conditionType
			patientScope
			monitoringType
			deltaType
			treatmentDurationAspect
			discontinuationReason
			occurence
			intervention
			reintervention
			consideredSecInterventionRate
			uoMType
			rollingRangeType
			isNamedTimeRangeUsed
			consideredRating
			startingPoint
			timeframeRangeType
			timeframeRelation
			timeframeStart
			timeframeEnd
			countdownPoint
			comparedStartingPoint
			comparedTimeframeRangeType
			comparedTimeframeRelation
			comparedTimeframeStart
			comparedTimeframeEnd
			comparedCountdownPoint
			operator
			bothOperatorFirst
			bothOperatorSecond
			value
			endRangeValue
			brands {
				Name
			}
			treatments {
				...TreatmentFields
			}
			generics {
				Name
			}
			responseType
			responseInclusionType
			conditionStatus
			adverseEvents
			grades
			amountOfThresholdBasis
			article71Status
			patentStatus
			eventCategory
			eventSubCategories
			eventType
			eventFrequency
			eventResult
		}
		actions(options: { sort: [{ order: ASC }] }) {
			order
			actionType
			actionSet
			value
			priceType
			adHocPriceTypes
			itemScope
			isAdHocDiscount
			packOptionType
			entityPriceType
			rollingRangeType
			actionLogicalOperator
			uoMType
			ingredientRangeType
			ingredientStartItemNumber
			ingredientEndItemNumber
			isNamedTimeRangeUsed
			itemRangeType
			startItemNumber
			endItemNumber
			timeframeRangeType
			timeframeStart
			timeframeEnd
			startingPoint
			itemCombinationRule
			combinedItemTimeframeRangeType
			combinedItemTimeframeStart
			combinedItemTimeframeEnd
			itemCombinationRelation
			brandsConnection {
				edges {
					order
					node {
						Name
					}
				}
			}
			treatmentsConnection {
				edges {
					order
					node {
						...TreatmentFields
					}
				}
			}
			genericsConnection {
				edges {
					order
					node {
						Name
					}
				}
			}
			combinedItemBrands {
				Name
			}
			combinedItemTreatments {
				...TreatmentFields
			}
			combinedItemGenerics {
				Name
			}
			cycle {
				order
				name
			}
			recordNature
		}
	}
`;

const casesFragment = gql`
	fragment RelevantCasesProps on Case {
		id
		creationDate
		actualAgreementId
		sourceTenantId
		status
		canUserViewFinancials
		flexEntitiesConnection(where: { node: { isDeleted: false } }) {
			edges {
				isStartingPoint
				node {
					id
					createdOn
					rating
					data
					tenantId
					documentStatus
					type {
						id
					}
				}
			}
		}
		negotiationRounds {
			status
			data
			source {
				id
			}
		}
		history {
			status
		}
	}
`;

export const GET_AGREEMENT_CASES = gql`
	${casesFragment}

	query GetAgreementCases($agreementIds: [ID!]!) {
		cases(where: { baseAgreement: { id_IN: $agreementIds } }) {
			...RelevantCasesProps
		}
	}
`;

export const baseAgreementProps = gql`
	${treatmentFieldsFragment}
	${preparationFieldsFragment}
	${scenarioFieldsFragment}
	${treatmentSetFieldsFragment}

	fragment BaseAgreementProps on BaseAgreement {
		id
		name
		pushedOn
		isLastVersion
		initialVersionId
		isHidden
		status
		tags {
			id
			name
			type
		}
		negotiableAttributes {
			identifier
		}
		formattedTags
		canUserViewFinancials
		scenarios(options: { sort: { order: ASC } }) {
			...ScenarioFields
		}
		treatmentPricesConnection {
			edges {
				basePriceType
				price
				node {
					...TreatmentFields
				}
			}
		}
		enrollingConditionSets {
			conditions {
				conditionType
				operator
				rangeInputStart
				rangeInputEnd
				inclusionType
				includedGenerics {
					Name
				}
				timeframeRangeType
				timeframeStartingPoint
				timeframeRangeStart
				timeframeRangeEnd
				countdownOperator
				resultSelectionType
				cycleLimitFrom
				cycleLimitTo
				rating
				therapyDecision
			}
			scopesConnection {
				edges {
					excludedOn
					node {
						scopeId
						code
						logicalOperator
						treatmentSets {
							...TreatmentSetFieldsFragment
						}
						diseases {
							Icd10Code
							Name
						}
						indications {
							id
							code
							type
							intent
							line
							mutation
							typeLocalizationKey
							intentLocalizationKey
							lineLocalizationKey
							mutationLocalizationKey
						}
						isDeleted
					}
				}
			}
			isDiseaseNotEnrollingCondition
			isIndicationNotEnrollingCondition
		}
		currency {
			Code
			Name
		}
		counterpartiesConnection {
			edges {
				role
				node {
					name
				}
			}
		}
		allowedForms
		requiredForms
	}
`;

export const whereDevices: AgreementScopeWhere = {
	enrollingConditionSets: {
		agreement: { subjectMatter: AgreementSubjectMatter.DEVICES, tags: { type: AgreementTagType.CUSTOM } },
	},
};
export const whereDrugs = (flexEntityTypeId: string, tenantId: string): AgreementScopeWhere => ({
	enrollingConditionSets: {
		agreement: {
			subjectMatter: AgreementSubjectMatter.DRUGS,
			dataContainers: { subType_IN: [flexEntityTypeId], providers: { id: tenantId } },
			OR: [{ allowedForms_INCLUDES: flexEntityTypeId }, { allowedRecords_INCLUDES: flexEntityTypeId }],
		},
	},
});

export const GET_SCOPES = gql`
	${preparationFieldsFragment}
	${treatmentFieldsFragment}
	${treatmentSetFieldsFragment}

	query GetEnrollingConditionSets($where: AgreementScopeWhere!) {
		agreementScopes(where: $where) {
			scopeId
			code
			logicalOperator
			treatmentSets {
				...TreatmentSetFieldsFragment
			}
			diseases {
				Icd10Code
				Name
			}
			indications {
				id
				code
				type
				intent
				line
				mutation
				typeLocalizationKey
				intentLocalizationKey
				lineLocalizationKey
				mutationLocalizationKey
			}
			isDeleted
			countryScopesConnection {
				edges {
					countryScopeId
					node {
						name
					}
				}
			}
			orgScopes {
				orgId
				organizationScopeId
			}
			enrollingConditionSets {
				isIndicationNotEnrollingCondition
				isDiseaseNotEnrollingCondition
				agreement {
					initialVersionId
					allowedForms
					allowedRecords
					currency {
						Code
					}
					country {
						name
					}
				}
			}
		}
	}
`;

export const GET_LEGAL_ENTITIES = gql`
	query GetLegalEntitiesFragment {
		organizations {
			id
			name
		}
	}
`;

export const GET_PREVIOUS_INTERVENTION_PATIENTS = gql`
	query GetPreviousInterventionPatients($previousInterventionTypeId: ID!) {
		previousInterventionPatients: patients(
			where: { assignedFlexEntities_SOME: { status: SUBMITTED, type: { id: $previousInterventionTypeId } } }
		) {
			insuranceId
		}
	}
`;

export const GET_PATIENT_ENTITIES_BY_ENTITY_ID = gql`
	query GetPatientEntitiesByEntityId($entityId: ID!) {
		patients(where: { assignedFlexEntities_SOME: { type: { containerType: FORM }, id: $entityId } }) {
			insuranceId
			assignedFlexEntities(where: { status: SUBMITTED, isDeleted: false, type: { containerType: FORM } }) {
				data
			}
		}
	}
`;

export const GET_PATIENT_ENTITIES_BY_ID = gql`
	query GetPatientEntitiesById($patientId: String!) {
		patients(where: { insuranceId: $patientId }) {
			insuranceId
			assignedFlexEntities(where: { type: { containerType: FORM, idPrefix: "CA" }, status: SUBMITTED }) {
				data
			}
		}
	}
`;

export const GET_GENERICS_BY_NAME = gql`
	query GetGenericsByName($name: String!) {
		generics(where: { Name_CONTAINS: $name }) {
			Name
		}
	}
`;

export const GET_DISEASES_BY_TOKEN = gql`
	query GetDiseasesByToken($token: String!) {
		diseases(where: { OR: [{ Icd10Code_MATCHES: $token }, { Name_MATCHES: $token }] }, options: { limit: 100 }) {
			Icd10Code
			Name
		}
	}
`;

export const GET_PRICE_PREVIEW_BY_AGREEMENT_IDS = gql`
	query GetPricePreviewByAgreementIds($agreementIds: [ID!]!) {
		baseAgreements(where: { id_IN: $agreementIds }) {
			pushedOn
			treatmentPricesConnection {
				edges {
					price
				}
			}
		}
	}
`;

export const GET_BRAND_BY_QUEST = gql`
	query GetBrandByQuest($id: ID!) {
		quests(where: { id: $id }) {
			recordsRelationship {
				id
				administrationDate
				status
				insuranceId
			}
			brand {
				Name
			}
		}
	}
`;

export const GET_DISCONTINUATION_NOTE_ADMINISTRATIONS = gql`
	query GetDiscontinuationNoteAdministrations($caseId: ID!, $discontinuationDate: Date!) {
		GetDiscontinuationNoteAdministrations(caseId: $caseId, discontinuationDate: $discontinuationDate) {
			administrationsAfterDnWithDocument
			administrationsAfterDnWithoutDocument
		}
	}
`;

export const GET_MATCHED_AGREEMENT_BY_CPDI = gql`
	${baseAgreementProps}

	query GetMatchedAgreement($preparationBrandNames: [String!]!, $genericNames: [String!]!, $flexEntityTypeId: String!) {
		baseAgreements(
			where: {
				AND: [
					{
						enrollingConditionSets_SOME: {
							scopes_SOME: {
								OR: [
									{ treatmentSets_SOME: { preparations_SOME: { BrandName_IN: $preparationBrandNames } } }
									{ treatmentSets_SOME: { generic: { Name_IN: $genericNames } } }
								]
							}
						}
						OR: [{ status: APPROVED }, { status: RUNNING }]
						isHidden: false
						isDeleted: false
					}
					{ allowedForms_INCLUDES: $flexEntityTypeId }
				]
			}
		) {
			...BaseAgreementProps
		}
	}
`;

export const GET_MATCHED_AGREEMENT_BY_SCOPEID = gql`
	${baseAgreementProps}

	query GetMatchedAgreementByScopeId($scopeId: String!, $flexEntityTypeId: String!) {
		baseAgreements(
			where: {
				AND: [
					{
						OR: [
							{
								enrollingConditionSets_SOME: {
									scopes_SOME: { countryScopesConnection_SOME: { edge: { countryScopeId: $scopeId } } }
								}
								OR: [{ status: APPROVED }, { status: RUNNING }]
								isHidden: false
								isDeleted: false
							}
							{
								enrollingConditionSets_SOME: {
									scopes_SOME: { organizationScopesConnection_SOME: { edge: { organizationScopeId: $scopeId } } }
								}
								OR: [{ status: APPROVED }, { status: RUNNING }]
								isHidden: false
								isDeleted: false
							}
						]
					}
					{ allowedForms_INCLUDES: $flexEntityTypeId }
				]
			}
		) {
			...BaseAgreementProps
		}
	}
`;
